import React from 'react';
import styles from './input.module.scss';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'date' | 'time';
  placeholder?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  icon?: React.ReactNode;
  onIconClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
   variant?: 'default' | 'solid' | 'light';
}

const NO_TRIM_TYPES = new Set(['password', 'number', 'date', 'time']);

export const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  type = 'text',
  placeholder,
  value,
  onChange,
  name,
  required = false,
  disabled = false,
  error,
  icon,
  onIconClick,
  className = '',
  height,
  variant = 'default',
  onBlur: onBlurFromProps,
  ...props
}, ref) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!NO_TRIM_TYPES.has(type) && onChange) {
      const trimmed = e.target.value.trim();
      if (trimmed !== e.target.value) {
        e.target.value = trimmed;
        onChange(e as unknown as React.ChangeEvent<HTMLInputElement>);
      }
    }
    onBlurFromProps?.(e);
  };

  return (
    <div className={`${styles.inputWrapper} ${className}`}>
      <input
        ref={ref}
        type={type}
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={`${styles.input} ${styles[variant]} ${error ? styles.error : ''} ${
          icon ? styles.hasIcon : ''
        }`}
        {...props}
      />
      {icon && (
        <button
          type="button"
          className={styles.iconButton}
          onClick={onIconClick}
          onMouseDown={(e) => e.preventDefault()}
          tabIndex={-1}
        >
          {icon}
        </button>
      )}
      {error && <span className={styles.errorMessage}>{error}</span>}
    </div>
  );
});